import ComponentError from 'components/Error/ComponentError';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import NoEnrollmentFound from 'components/NoEnrollment/NoEnrollmentFound';
import {
  Alert,
  AlertGroup,
  AlertActionCloseButton,
  AlertVariant,
  AlertActionLink,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import useMobxStores from 'hooks/useMobxStores';
import { useLocalStorageState } from '@ole-ui/ole-ui-components';

const GenericAlert = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'generic-alert' */ 'components/Banners/GenericAlert'
    ),
);

const ComplianceBannerDialog = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'compliance-banner-dialog' */ 'views/Home/HomeV1/ComplianceBanner'
    ),
);

const PrivacyBannerDialog = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'privacy-banner-dialog' */ 'views/Home/HomeV1/PrivacyBanner'
    ),
);

const PremiumIntroBannerDialog = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'premium-intro-banner-dialog' */ 'views/Home/HomeV1/PremiumIntroBanner'
    ),
);

const ConcurrentLoginBanner = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'concurrent-login-banner' */ 'components/Banners/ConcurrentLogin'
    ),
);

const PendingManagerForm = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'pending-manager-form-component' */ 'components/Dashboard/PendingManagerForm'
    ),
);

/**
 * Contains all the alerts and banners from the older home page.
 * This was mainly ported from the old code and needs to be refined over time.
 */
const HomeBanners = () => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const removeAlert = (alertKey) => {
    setAlerts((oldList) => oldList.filter((a) => a.key !== alertKey));
  };

  const addAlert = (alert) => {
    setAlerts((oldList) => [...oldList, alert]);
  };
  const {
    alertStore,
    classesStore,
    routerStore,
    userStore,
    eventStore,
    organizationStore,
  } = useMobxStores();

  const { shouldShowNoEnrollmentBanner } = classesStore;
  const showComplianceBannerDialog = userStore.isComplianceBannerDue;
  const showPrivacyBannerDialog = userStore.needsPrivacyBanner;
  const showPremiumIntroBannerDialog = userStore.needsPremiumIntroBanner;

  const concurrentLoginAlert = eventStore.isConcurrentLogin
    ? {
        onDismiss: eventStore.resetConcurrentLogin,
      }
    : null;

  const homeAlerts = alertStore.currentAlerts?.filter((alert) =>
    alert.environment.includes('home'),
  );

  const [isActiveUser, setActiveUser] = useLocalStorageState(
    'trialActiveUser',
    false,
  );

  useEffect(() => {
    if (
      routerStore.previousRoute?.name === 'trial-enroll' &&
      isActiveUser === true
    ) {
      let message = t(
        'Not eligible for the Red Hat Learning Subscription Trial',
      );
      let description = t(
        'You already have an active learning subscription with enhanced benefits. The Red Hat Learning Subscription Trial is unavailable with your current subscription',
      );
      if (userStore.isFreeTierSubscriber) {
        message = t('Red Hat Learning Subscription Trial is not yet available');
        description = t(
          'You have time remaining in your Red Hat Learning Subscription 14 day trial. Once the 14-day trial expires, you will then be able to access your Red Hat Learning Subscription Trial',
        );
      }

      addAlert({
        key: 'trial-active-user',
        message,
        variant: 'info',
        description,
      });
      setActiveUser(!isActiveUser);
    }
  }, [routerStore.previousRoute]);

  return (
    <div className="my-activity__banners">
      {showComplianceBannerDialog && <ComplianceBannerDialog />}
      {showPrivacyBannerDialog && <PrivacyBannerDialog />}
      {showPremiumIntroBannerDialog && <PremiumIntroBannerDialog />}
      {(organizationStore.organizationsList || [])
        .filter((org) => org.is_pending)
        .map((org) => (
          <PendingManagerForm key={org.doc_id} organization={org} />
        ))}
      {eventStore.isConcurrentLogin && (
        <ConcurrentLoginBanner {...concurrentLoginAlert} />
      )}
      {shouldShowNoEnrollmentBanner && <NoEnrollmentFound />}
      {homeAlerts?.map((alert) => (
        <GenericAlert {...alert} className="my-activity__alert" />
      ))}
      <div>
        {!!(alerts.length > 0) && (
          <div className="trail-active-alert-overlay" />
        )}
        <AlertGroup
          isToast
          isLiveRegion
          className="trail-active-user-group-alert"
        >
          {alerts.map((alert) => (
            <Alert
              variant={AlertVariant[alert.variant]}
              title={alert.message}
              actionClose={
                <AlertActionCloseButton
                  title={alert.title}
                  onClose={() => removeAlert(alert.key)}
                />
              }
              key={alert.key}
              actionLinks={
                <React.Fragment>
                  <AlertActionLink // eslint-disable-next-line no-console
                    onClick={() => removeAlert(alert.key)}
                  >
                    {t(`Close`)}
                  </AlertActionLink>
                </React.Fragment>
              }
            >
              {alert.description}
            </Alert>
          ))}
        </AlertGroup>
      </div>
    </div>
  );
};

export default withErrorBoundary(
  observer(HomeBanners),
  ComponentError,
  handleError,
);
